import { FC, useState } from 'react';
import { Dialog, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import {
    DialogContent,
    DialogTitle,
    CloseButtonContainer
} from '../../../../../../shared/ConfirmationDialog/styled';
import {
    DescriptionContent,
    MarkdownContainer,
    EditButtonContainer,
    ActionButtonsContainer,
    ActionButton,
    AttachmentsSection,
    AttachmentsGrid,
    TextFieldContainer,
    CloseIconStyles,
    EditIconButtonStyles,
    StyledDescriptionDialogPaper,
    EditModeAttachmentsContainer
} from './styled';
import AttachmentItem from '../../../../../../shared/AttachmentItem';
import AttachmentUpload from '../../../../../../shared/AttachmentUpload';
import AttachmentPopup from '../../../../../../shared/AttachmentPopup';
import { IAttachment } from 'src/types/attachments';
import { getViewUrl } from 'src/redux/attachments/service';
import useAppDispatch from 'src/hooks/useAppDispatch';

interface Props {
    open: boolean;
    onClose: () => void;
    description: string;
    attachments?: IAttachment[];
    onSave: (newDescription: string, attachmentIds: number[]) => Promise<void>;
}

const DescriptionReviewDialog: FC<Props> = ({
    open,
    onClose,
    description,
    attachments = [],
    onSave
}) => {
    const dispatch = useAppDispatch();
    const [isEditing, setIsEditing] = useState(false);
    const [editedDescription, setEditedDescription] = useState(description);
    const [isSaving, setIsSaving] = useState(false);
    const [existingAttachments, setExistingAttachments] = useState<
        IAttachment[]
    >([]);
    const [newAttachmentIds, setNewAttachmentIds] = useState<number[]>([]);
    const [popupState, setPopupState] = useState({
        isOpen: false,
        url: '',
        type: '',
        name: '',
        currentIndex: 0,
        fileId: 0 as number
    });

    const handleEdit = () => {
        setEditedDescription(description);
        setExistingAttachments(attachments);
        setNewAttachmentIds([]);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEditedDescription(description);
        setExistingAttachments([]);
        setNewAttachmentIds([]);
        setIsEditing(false);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const existingIds = existingAttachments.map(
                (att) => att.fileId ?? att.id
            );
            const allAttachmentIds = [...existingIds, ...newAttachmentIds];
            await onSave(editedDescription, allAttachmentIds);
            setIsEditing(false);
            setExistingAttachments([]);
            setNewAttachmentIds([]);
        } catch (error) {
            console.error('Failed to save description:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const removeExistingAttachment = (idToRemove: number) => {
        setExistingAttachments((prev) =>
            prev.filter(
                (att) => att.fileId !== idToRemove && att.id !== idToRemove
            )
        );
    };

    const handleClose = () => {
        setIsEditing(false);
        setExistingAttachments([]);
        setNewAttachmentIds([]);
        onClose();
    };

    const getAttachmentUrl = async (file: IAttachment, index: number) => {
        if (!file.fileId) {
            console.error('File ID is missing:', file);
            return;
        }
        try {
            const response = await dispatch(
                getViewUrl({ fileId: file.fileId })
            ).unwrap();
            setPopupState({
                isOpen: true,
                url: response.url,
                type: file.type,
                name: file.name,
                currentIndex: index,
                fileId: file.fileId
            });
        } catch (error) {
            console.error('Error fetching attachment URL:', error);
        }
    };

    const closePopup = () => {
        setPopupState((prev) => ({ ...prev, isOpen: false }));
    };

    const handleNextAttachment = async () => {
        if (!attachments || attachments.length === 0) return;
        const nextIndex = (popupState.currentIndex + 1) % attachments.length;
        await getAttachmentUrl(attachments[nextIndex], nextIndex);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="description-dialog-title"
                PaperComponent={StyledDescriptionDialogPaper}
            >
                <CloseButtonContainer>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={CloseIconStyles}
                    >
                        <CloseIcon />
                    </IconButton>
                </CloseButtonContainer>
                <DialogTitle id="description-dialog-title">
                    Project Description
                </DialogTitle>
                <DialogContent>
                    {!isEditing ? (
                        <>
                            <DescriptionContent>
                                <MarkdownContainer>
                                    {description ? (
                                        <MarkdownEditor
                                            value={description}
                                            readOnly
                                        />
                                    ) : (
                                        'No description available.'
                                    )}
                                </MarkdownContainer>
                                <EditButtonContainer>
                                    <IconButton
                                        onClick={handleEdit}
                                        size="small"
                                        sx={EditIconButtonStyles}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </EditButtonContainer>
                            </DescriptionContent>

                            {attachments.length > 0 && (
                                <AttachmentsSection>
                                    <AttachmentsGrid>
                                        {attachments.map((file, index) => (
                                            <AttachmentItem
                                                key={`attachment-${
                                                    file.id || index
                                                }`}
                                                file={{
                                                    ...file,
                                                    id:
                                                        file.fileId ||
                                                        file.id ||
                                                        index,
                                                    fileId:
                                                        file.fileId || file.id
                                                }}
                                                index={index}
                                                getAttachmentUrl={
                                                    getAttachmentUrl
                                                }
                                            />
                                        ))}
                                    </AttachmentsGrid>
                                </AttachmentsSection>
                            )}
                        </>
                    ) : (
                        <>
                            <TextFieldContainer>
                                <MarkdownEditor
                                    value={editedDescription}
                                    onChange={(v) =>
                                        setEditedDescription(v || '')
                                    }
                                    height={300}
                                />
                            </TextFieldContainer>

                            {existingAttachments.length > 0 && (
                                <EditModeAttachmentsContainer>
                                    {existingAttachments.map((file, index) => (
                                        <AttachmentItem
                                            key={`existing-${
                                                file.fileId || index
                                            }`}
                                            file={{
                                                ...file,
                                                id:
                                                    file.fileId ||
                                                    file.id ||
                                                    index,
                                                fileId: file.fileId || file.id
                                            }}
                                            index={
                                                file.fileId || file.id || index
                                            }
                                            removeAttachment={
                                                removeExistingAttachment
                                            }
                                        />
                                    ))}
                                </EditModeAttachmentsContainer>
                            )}

                            <AttachmentUpload
                                attachmentIds={newAttachmentIds}
                                setAttachmentIds={setNewAttachmentIds}
                                compact
                            />

                            <ActionButtonsContainer>
                                <ActionButton
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    variant="cancel"
                                >
                                    <CloseIcon fontSize="small" />
                                </ActionButton>
                                <ActionButton
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    variant="confirm"
                                >
                                    <CheckIcon fontSize="small" />
                                </ActionButton>
                            </ActionButtonsContainer>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <AttachmentPopup
                isOpen={popupState.isOpen}
                onClose={closePopup}
                url={popupState.url}
                type={popupState.type}
                name={popupState.name}
                currentIndex={popupState.currentIndex + 1}
                totalItems={attachments?.length || 0}
                onNext={handleNextAttachment}
                fileId={popupState.fileId}
            />
        </>
    );
};

export default DescriptionReviewDialog;
